import WizardBuilder from 'formiojs/WizardBuilder';
import { AddCustomFieldComponent } from '../../form-components/custom-field';
import { PopupWrapper } from '../../form-components/popup';
import { convertDataFieldToComponent } from '../../../utils/data-field-metadata/data-field-metadata-utils';
import _ from 'lodash';
import { ContextDataProperty, CustomComponentType, ExternalWizardEventType, LoanRoleCustomComponentTypes } from '../../../enums';
import { getWidgetType } from '../../../utils/form-path-utils';
import { getContextDataValue } from '../../../utils/context-data-helper/context-data-helper';
import { FeatureFlagging } from '@sageworks/jpi';
import dragula from 'dragula';

export default class ModifiedWizardBuilder extends WizardBuilder {
	private _showPageTools: boolean | undefined;

	protected get isOpenAiCustomJavascriptGenerationEnabled() {
		const featureFlags = getContextDataValue(this, ContextDataProperty.FeatureFlags) ?? [];
		return featureFlags.includes(FeatureFlagging.FeatureFlagEnum.EnableOpnaiCustomJavascriptGeneration);
	}

	public get showPageTools() {
		if (this._showPageTools == null) {
			const widgetType = getWidgetType(this.form.path);
			this._showPageTools = widgetType != null ? !LoanRoleCustomComponentTypes.includes(widgetType) : true;
		}

		return this._showPageTools ?? true;
	}

	attach(element: HTMLElement) {
		// Call parent attach first
		const result = super.attach(element);

		// Then add our custom functionality
		this.loadRefs(element, {
			addCustomField: 'single',
			searchComponents: 'multiple',
			searchIcon: 'multiple',
			gotoPage: 'multiple'
		});

		this.addEventListener(this.refs.addCustomField as any, 'click', () => this.addCustomField());

		this.refs.searchComponents.forEach(searchInput => {
			this.addEventListener(searchInput, 'input', this.debouncedSearchComponents(searchInput));
		});

		this.refs.searchIcon.forEach(icon => {
			this.addEventListener(icon, 'click', this.clearSearch(icon));
		});

		// Add drag info to page elements (refs.gotoPage is loaded by parent)
		if (this.refs.gotoPage) {
			(this.refs.gotoPage as any[]).forEach((page: any, index: number) => {
				page.parentNode.dragInfo = { index };
			});

			// Initialize dragula for page reordering
			if (dragula) {
				const wizardPagesContainer = element.querySelector('.wizard-pages');
				if (wizardPagesContainer) {
					dragula([wizardPagesContainer]).on('drop', this.onReorder.bind(this));
				}
			}
		}

		return result;
	}

	onReorder(el: any, target: any, source: any, sibling: any) {
		if (!el || !el.dragInfo || !this.form || !this.form.components) {
			return;
		}

		const oldIndex = el.dragInfo.index;
		let newIndex = 0;

		// Find the new index based on the sibling
		if (sibling && sibling.dragInfo) {
			newIndex = sibling.dragInfo.index;
		} else {
			// If there's no sibling, it was dropped at the end
			newIndex = this.form.components.length - 1;
		}

		if (oldIndex === newIndex) {
			return;
		}

		// Reorder the components array
		const movedComponent = this.form.components.splice(oldIndex, 1)[0];
		this.form.components.splice(newIndex, 0, movedComponent);

		// Rebuild the wizard to reflect the new order
		this.rebuild();
	}

	private debouncedSearchComponents(searchInput: HTMLElement) {
		return _.debounce(event => {
			event.stopPropagation();

			this.updateFilteredComponents(searchInput);
		}, 500);
	}

	private updateFilteredComponents(searchInput: HTMLElement) {
		for (const groupKey in this.groups) {
			const group = this.groups[groupKey];
			group.default = false;
		}

		const groupKey = searchInput.getAttribute('data-group-key') as string;
		const group = this.groups[groupKey];
		group.default = true;
		group.searchValue = (searchInput as any).value;
		const searchValue = group.searchValue?.toLowerCase() ?? '';
		for (const componentKey in group.components) {
			const component = group.components[componentKey];
			const componentMatches = component.title?.toLowerCase().includes(searchValue);
			component.hidden = !componentMatches;
		}

		return this.redraw().then(() => {
			this.focusSearch(groupKey);
		});
	}

	private clearSearch(icon: HTMLElement) {
		return (event: Event) => {
			event.stopPropagation();

			const groupKey = icon.getAttribute('data-group-key') as string;
			const group = this.groups[groupKey];
			if (group.searchValue.length > 0) {
				this.refs.searchComponents.forEach(input => {
					if (input.getAttribute('data-group-key') === groupKey) {
						(input as any).value = '';
						return this.updateFilteredComponents(input);
					}
				});
			}
			return Promise.resolve();
		};
	}

	private focusSearch(groupKey: string) {
		this.refs.searchComponents.forEach(input => {
			if (input.getAttribute('data-group-key') === groupKey) {
				input.focus();
				(input as any).setSelectionRange(999, 999);
			}
		});
	}

	private async addCustomField() {
		let addCustomFieldComponent = new AddCustomFieldComponent(
			AddCustomFieldComponent.schema({
				path: this.form.path
			}),
			{ parent: this },
			this.dataValue
		);
		let popupWrapper = new PopupWrapper({}, {}, {}, addCustomFieldComponent);

		const addedField = await popupWrapper.showPopup();

		if (addedField != null) {
			this.emit(ExternalWizardEventType.customFieldAdded, this);
			this.groups.udFields.componentOrder.push(addedField.id);
			this.groups.udFields.components[addedField.id] = {
				key: addedField.id,
				...convertDataFieldToComponent(addedField, { weight: 0 })
			};
			this.redraw();
		}
	}

	saveComponent(component: any, parent: any, isNew: any, original: any) {
		super.saveComponent(component, parent, isNew, original);

		// Update saved components to force delete confirmations after initial save
		this.on(
			'saveComponent',
			(component: any) => {
				if (component.skipRemoveConfirm && Object.values(CustomComponentType).includes(component.type)) {
					delete component.skipRemoveConfirm;
				}
			},
			false,
			true
		);
	}

	editComponent(component: any, parent: any, isNew: boolean, isJsonEdit: boolean, original: any): void {
		if (!this.isOpenAiCustomJavascriptGenerationEnabled) {
			_.set(this.options, `editForm.${component.type}`, [
				{
					key: 'scriptAi',
					hidden: true
				}
			]);
		}

		super.editComponent(component, parent, isNew, isJsonEdit, original);

		if (isJsonEdit) {
			this.editForm.form.components[0].validate['custom'] =
				'if(self.editors && self.editors.length > 0) { ' +
				'return self.editors[0].getSession().getAnnotations().length > 0 ? "Please fix any JSON syntax errors before saving" : true' +
				' } return true;';
		}
	}
}
